<template>
  <div class="highlight-sns-post-menu-parts">
    <div class="highlight-sns-post-menu-parts__inner">
      <div class="highlight-sns-post-menu-parts__block">
        <div class="highlight-sns-post-menu-parts__header">
          <p class="highlight-sns-post-menu-parts__title">
            {{ $tc('RaceVideoPage.highlights.sns.xAndInstagram.titleOnlyX') }}
          </p>
          <button
            class="highlight-sns-post-menu-parts__button-close"
            @click="$emit('onClickClose')"
          >
            <span class="highlight-sns-post-menu-parts__button-close-icon">
              <highlights-cancel-icon />
            </span>
          </button>
        </div>
        <div class="highlight-sns-post-menu-parts__body">
          <p class="highlight-sns-post-menu-parts__message">
            {{ $tc('RaceVideoPage.highlights.sns.xAndInstagram.commonMessageOnlyX') }}
          </p>
          <p v-if="!isComplete" class="highlight-sns-post-menu-parts__message">
            {{ downloadMessage }}
          </p>

          <div class="highlight-sns-post-menu-parts__tool">
            <p v-if="isError" class="highlight-sns-post-menu-parts__tool-error">
              {{ $tc('RaceVideoPage.highlights.sns.xAndInstagram.downloadError') }}
            </p>
            <atom-button
              v-if="displayDownloadPreparationButton"
              :disabled="!canClipTargetOperationHighlightForPc"
              :theme="'dark'"
              :linkText="$tc('RaceVideoPage.highlights.sns.xAndInstagram.download')"
              :colorType="'secondary'"
              @onClick="handleDownloadPreparationClicked"
            />
            <p v-if="isInProgress" class="highlight-sns-post-menu-parts__download">
              {{ $tc('RaceVideoPage.highlights.sns.xAndInstagram.preparing') }}
            </p>
            <div v-if="isComplete" class="highlight-sns-post-menu-parts__downloaded">
              <p class="highlight-sns-post-menu-parts__downloaded-message">
                {{ $tc('RaceVideoPage.highlights.sns.xAndInstagram.ready') }}
              </p>
              <button
                class="highlight-sns-post-menu-parts__button-download"
                @click="handleDownloadClicked"
              >
                <span class="highlight-sns-post-menu-parts__button-download-label">{{
                  $tc('RaceVideoPage.highlights.sns.xAndInstagram.toDownload')
                }}</span>
                <span class="highlight-sns-post-menu-parts__button-download-icon">
                  <highlights-download-icon />
                </span>
              </button>
            </div>
          </div>

          <ul class="highlight-sns-post-menu-parts__list">
            <li class="highlight-sns-post-menu-parts__list-item">
              <a
                class="highlight-sns-post-menu-parts__button-sns"
                :class="{
                  'highlight-sns-post-menu-parts__button-sns--disabled': !isComplete,
                }"
                href="javascript:void(0)"
                @click="onClickAnchorLink(xUrl)"
              >
                <span class="highlight-sns-post-menu-parts__button-sns-label">{{
                  $tc('RaceVideoPage.highlights.sns.xAndInstagram.post.x')
                }}</span>
                <span class="highlight-sns-post-menu-parts__button-sns-icon">
                  <highlights-external-link-icon />
                </span>
              </a>
            </li>
            <li v-if="false" class="highlight-sns-post-menu-parts__list-item">
              <a
                class="highlight-sns-post-menu-parts__button-sns"
                :class="{
                  'highlight-sns-post-menu-parts__button-sns--disabled': !isComplete,
                }"
                href="javascript:void(0)"
                @click="onClickAnchorLink(instagramUrl)"
              >
                <span class="highlight-sns-post-menu-parts__button-sns-label">{{
                  $tc('RaceVideoPage.highlights.sns.xAndInstagram.post.instagram')
                }}</span>
                <span class="highlight-sns-post-menu-parts__button-sns-icon">
                  <highlights-external-link-icon />
                </span>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from '@vue/composition-api'
import AtomButton from '@/components/atoms/AtomButton.vue'
import HighlightsExternalLinkIcon from '@/components/RaceVideoPage/RaceVideoHighlightsPane/parts/icons/HighlightsExternalLinkIcon.vue'
import HighlightsCancelIcon from '@/components/RaceVideoPage/RaceVideoHighlightsPane/parts/icons/HighlightsCancelIcon.vue'
import HighlightsDownloadIcon from '@/components/RaceVideoPage/RaceVideoHighlightsPane/parts/icons/HighlightsDownloadIcon.vue'
import SceneMoviesDocument from '@/store/stores/collectionModule/documents/highlight/SceneMoviesDocument'
import I18n from '@/locales/I18n'
import Const from '@/util/Const'

export default defineComponent({
  name: 'HighlightSnsPostMenuParts',
  props: {
    /**
     * 動画集
     */
    sceneMovies: {
      type: Object as PropType<SceneMoviesDocument | null>,
      default: () => ({}),
    },
    /**
     * ハイライトの切り抜きが可能かどうか
     */
    canClipTargetOperationHighlightForPc: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    HighlightsDownloadIcon,
    HighlightsCancelIcon,
    HighlightsExternalLinkIcon,
    AtomButton,
  },
  setup(props, context) {
    // XのURL
    const xUrl = Const.EXTERNAL_LINKS.X.HOME
    // InstagramのURL
    const instagramUrl = Const.EXTERNAL_LINKS.INSTAGRAM.HOME

    // ダウンロード準備ボタンを表示するかどうか
    const displayDownloadPreparationButton = computed(
      () =>
        !(
          props.sceneMovies &&
          (props.sceneMovies.status === 'InProgress' || props.sceneMovies.status === 'Complete')
        ),
    )
    // ハイライト動画切り抜き：ダウンロード準備中かどうか
    const isInProgress = computed(() => props.sceneMovies?.status === 'InProgress')
    // ハイライト動画切り抜き：ダウンロード準備が完了しているかどうか
    const isComplete = computed(() => props.sceneMovies?.status === 'Complete')
    // ハイライト動画切り抜き：エラーかどうか
    const isError = computed(() => props.sceneMovies?.status === 'Error')

    /** ダウンロードメッセージ */
    const downloadMessage = computed(() =>
      props.canClipTargetOperationHighlightForPc
        ? I18n.tc('RaceVideoPage.highlights.sns.xAndInstagram.messageCanDownload')
        : I18n.tc('RaceVideoPage.highlights.sns.xAndInstagram.messageCannotDownload'),
    )

    /**
     * ダウンロード準備を開始する
     */
    const handleDownloadPreparationClicked = () => {
      context.emit('startDownloadPreparation')
    }
    /**
     * ダウンロード可能状態でユーザーがダウンロードボタンを押した際にファイルをダウンロードする
     */
    const handleDownloadClicked = async () => {
      context.emit('downloadHighlight', props.sceneMovies)
    }

    /**
     * SNS投稿ボタンをクリックした場合に呼び出される。
     * @param url 外部サイトのリンク
     */
    const onClickAnchorLink = (url: string) => {
      // 外部リンクを開く
      window.open(url, '_blank')
      context.emit('onSaveMissionHistory')
    }

    return {
      xUrl,
      instagramUrl,
      displayDownloadPreparationButton,
      isInProgress,
      isError,
      isComplete,
      downloadMessage,
      handleDownloadPreparationClicked,
      handleDownloadClicked,
      onClickAnchorLink,
    }
  },
})
</script>

<style scoped lang="scss">
.highlight-sns-post-menu-parts {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 41;
  background-color: $black70;

  &__inner {
    position: absolute;
    top: 0;
    right: 0;
    overflow: hidden;
    width: 375px;
    height: calc(100% - 220px);
  }

  &__block {
    position: absolute;
    left: 50%;
    bottom: 6px;
    width: 363px;
    background-color: $black20;
    border-radius: 8px;
    transform: translateX(-50%);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 44px;
    box-shadow: 0 1px 0 0 rgba($black, 0.05);
  }

  &__title {
    font-size: 14px;
    font-weight: 700;
    color: $white;
    line-height: 1.43;
    letter-spacing: 0.02em;
  }

  &__body {
    padding: 12px 10px;
    border-top: 1px solid $white10;
  }

  &__message {
    font-size: 13px;
    font-weight: 500;
    color: $white70;
    line-height: 1.54;
    letter-spacing: 0.04em;
    text-align: center;
    white-space: pre-wrap;
  }

  &__tool {
    position: relative;
    padding: 20px 0;
    margin-top: 10px;

    &:has(.highlight-sns-post-menu-parts__tool-error) {
      padding: 5px 0;
    }

    &-error {
      padding: 3px 5px;
      margin-bottom: 2px;
      font-size: 12px;
      font-weight: 500;
      color: $white70;
      text-align: center;
      background-color: $black70;
      border: 1px solid $red-light;
      border-radius: 32px;
      backdrop-filter: blur(2.5px);
    }
  }

  &__download {
    padding: 14px 0;
    font-size: 13px;
    font-weight: 700;
    color: $white70;
    letter-spacing: 0.04em;
    line-height: 1.54;
    text-align: center;
  }

  &__downloaded {
    &-message {
      position: absolute;
      top: 0;
      left: 50%;
      width: 100%;
      font-size: 13px;
      font-weight: 500;
      color: $white;
      line-height: 1.54;
      text-align: center;
      transform: translateX(-50%);
    }
  }

  &__list {
    margin-top: 10px;

    &-item {
      + .highlight-sns-post-menu-parts__list-item {
        margin-top: 10px;
      }
    }
  }

  &__button {
    &-download {
      display: flex;
      gap: 2px;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 48px;
      font-size: 13px;
      font-weight: 700;
      color: $green-light;
      letter-spacing: 0.04em;
      line-height: 1.54;

      &-icon {
        width: 18px;
        height: 18px;
        transform: translateY(-2px);
      }
    }

    &-close {
      position: absolute;
      top: 4px;
      right: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      cursor: pointer;

      &-icon {
        display: flex;
        width: 24px;
        height: 24px;
        color: $white;
        opacity: 0.5;
      }
    }

    &-sns {
      display: flex;
      gap: 4px;
      align-items: center;
      justify-content: center;
      height: 48px;
      color: $green-light;
      background-color: rgba($black, 0.5);
      border: 1px solid rgba($green-light, 0.9);
      border-radius: 4px;

      &--disabled {
        color: $white50;
        border-color: $white50;
        pointer-events: none;
      }

      &-label {
        font-size: 13px;
        font-weight: 700;
        line-height: 1.54;
        letter-spacing: 0.04em;
      }

      &-icon {
        width: 12px;
        height: 12px;
      }
    }
  }
}
</style>
